import { useEffect } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { CheckCircle2, ArrowUpRight, MapPin } from "lucide-react";
import { CITIES, CITY_LIST } from "@/data/cities";
import Reveal from "@/components/Reveal";

export default function CityPage() {
    const location = useLocation();
    const slug = location.pathname.replace("/renovation-", "");
    const data = CITIES[slug];

    useEffect(() => {
        if (data) {
            document.title = `${data.title} | Japet Agencement`;
            let meta = document.querySelector('meta[name="description"]');
            if (!meta) {
                meta = document.createElement("meta");
                meta.name = "description";
                document.head.appendChild(meta);
            }
            meta.content = `${data.intro.slice(0, 150)}…`;
        }
    }, [data]);

    if (!data) return <Navigate to="/" replace />;

    const sister = CITY_LIST.filter((c) => c.slug !== data.slug).slice(0, 4);
    const ldJson = {
        "@context": "https://schema.org",
        "@type": "Service",
        "serviceType": "Rénovation d'intérieur haut de gamme",
        "provider": { "@type": "LocalBusiness", "name": "Japet Agencement", "areaServed": data.city },
        "areaServed": data.city,
        "description": data.intro,
    };

    return (
        <div data-testid="city-page">
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJson) }} />

            <section className="max-w-7xl mx-auto px-6 md:px-12 py-20">
                <Reveal>
                    <span className="overline flex items-center gap-2"><MapPin size={14} /> SEO local</span>
                    <h1 className="mt-6 font-serif text-5xl md:text-7xl lg:text-8xl font-light text-brand-ink leading-[1.02] max-w-5xl">
                        Rénovation à
                        <span className="italic block text-brand-sage">{data.city}.</span>
                    </h1>
                    <p className="mt-8 text-xl text-brand-sageDark font-light max-w-3xl leading-relaxed">{data.intro}</p>
                </Reveal>
            </section>

            <section className="bg-brand-bgalt py-20 md:py-28">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
                    <div className="md:col-span-7">
                        <span className="overline">Notre savoir-faire à {data.city}</span>
                        <h2 className="mt-5 font-serif text-4xl md:text-5xl font-light text-brand-ink leading-tight">
                            Une rénovation
                            <span className="italic"> sur-mesure </span>
                            pour votre quartier.
                        </h2>
                        <p className="mt-8 text-lg text-brand-sageDark font-light leading-relaxed">{data.focus}</p>
                        <ul className="mt-10 space-y-4">
                            {[
                                "Coordination tous corps d'état — un seul interlocuteur",
                                "Devis détaillé poste par poste, sans surprise",
                                "Engagement contractuel sur les délais et budget",
                                "Garantie décennale + responsabilité civile professionnelle",
                                "Respect du voisinage et du règlement de copropriété",
                            ].map((item) => (
                                <li key={item} className="flex items-start gap-3 text-brand-ink">
                                    <CheckCircle2 size={18} className="text-brand-sage shrink-0 mt-0.5" strokeWidth={1.5} />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="md:col-span-4 md:col-start-9 self-start sticky top-44 bg-brand-bg border border-brand-border p-8">
                        <span className="overline">Démarrer votre projet</span>
                        <h3 className="mt-4 font-serif text-2xl text-brand-ink font-light">Devis personnalisé à {data.city}</h3>
                        <p className="mt-3 text-sm text-brand-sageDark">Étude gratuite, sans engagement. Réponse sous 48h ouvrées.</p>
                        <Link
                            to="/devis"
                            data-testid="city-cta-devis"
                            className="mt-6 w-full inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-brand-ink hover:bg-brand-sage text-brand-bg text-xs uppercase tracking-[0.22em] transition-colors"
                        >
                            Demander un devis <ArrowUpRight size={14} />
                        </Link>
                        {data.landmarks?.length > 0 && (
                            <div className="mt-7 pt-5 border-t border-brand-border">
                                <p className="text-xs uppercase tracking-[0.18em] text-brand-sage mb-3">Nous intervenons aussi à</p>
                                <ul className="space-y-1 text-sm text-brand-sageDark">
                                    {data.landmarks.map((l) => <li key={l}>· {l}</li>)}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 md:px-12 py-20">
                <h2 className="font-serif text-3xl md:text-4xl text-brand-ink font-light text-center mb-12">
                    Nous intervenons aussi
                    <span className="italic"> dans ces communes</span>
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {sister.map((s) => (
                        <Link
                            key={s.slug}
                            to={`/renovation-${s.slug}`}
                            data-testid={`city-link-${s.slug}`}
                            className="group p-6 bg-brand-bgalt border border-brand-border hover:border-brand-sage transition-colors"
                        >
                            <p className="font-serif text-xl text-brand-ink group-hover:text-brand-sage transition-colors">{s.city}</p>
                            <p className="mt-2 text-sm text-brand-sageDark line-clamp-2">{s.intro.slice(0, 80)}…</p>
                        </Link>
                    ))}
                </div>
            </section>
        </div>
    );
}
